import React, { useEffect, useRef } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../customHooks/useAuth';
import { Copilot } from '../modules/Copilot';
import { signOut, saveIntendedRoute } from '../services/authService';
import { useSelector } from 'react-redux';
import { RootState } from '../store';
import Loader from '../common/components/Loader';
import { IUser } from '../common/types/common';

const ProtectedRoute: React.FC = () => {
  const isAuthenticated = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const hasProcessedRoute = useRef(false);
  const orgs = useSelector((state: RootState) => state.organisation);

  useEffect(() => {
    if (!hasProcessedRoute.current) {
      const intendedRoute = `${location.pathname}${location.search}${location.hash}`;

      if (
        !location.pathname.includes('/login') &&
        !location.pathname.includes('/auth/callback') &&
        location.pathname !== '/'
      ) {
        saveIntendedRoute(intendedRoute);
      }

      hasProcessedRoute.current = true;
    }

    if (!isAuthenticated) {
      setTimeout(() => {
        signOut();
        navigate('/login');
      }, 10);
    }
  }, [isAuthenticated, location, navigate]);

  // Don't render anything if not authenticated
  if (!isAuthenticated) {
    return null;
  }

  const isDataXplr = location.pathname.startsWith('/data-xplr');

  if (!orgs || orgs.length === 0) {
    if (isDataXplr) {
      return <Outlet />;
    }
    return <Loader message="Loading…" />;
  }

  return (
    <>
      <Outlet />
      <Copilot position="bottom-right" />
    </>
  );
};

// Mapping between navigation paths and user_modules keys
const pathToModuleMap: Record<string, keyof NonNullable<IUser['user_modules']>> = {
  '/': 'dashboard',
  '/process': 'process_xplr',
  '/ideation-xplr': 'ideation_xplr',
  '/solution-xplr': 'solution_xplr',
  '/portfolio': 'portfolio_xplr',
  '/data-xplr': 'data_xplr',
};

interface ModuleProtectedRouteProps {
  children: React.ReactNode;
  path: string;
}

/**
 * Component that protects routes based on user_modules permissions.
 * Redirects to the first available module if the user doesn't have access.
 */
export const ModuleProtectedRoute: React.FC<ModuleProtectedRouteProps> = ({ children, path }) => {
  const user = useSelector((state: RootState) => state.user);
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    // Determine the module key from the path prop or actual location
    // For dynamic routes like /data-xplr/:id, check if location starts with the base path
    let moduleKey: keyof NonNullable<IUser['user_modules']> | undefined;

    // First try to get module key from the path prop
    moduleKey = pathToModuleMap[path];

    // If not found, check if location pathname starts with any module path
    if (!moduleKey) {
      const matchingPath = Object.keys(pathToModuleMap).find((modulePath) => {
        // Handle exact match or pathname starting with module path (for dynamic routes)
        return location.pathname === modulePath || location.pathname.startsWith(`${modulePath}/`);
      });
      if (matchingPath) {
        moduleKey = pathToModuleMap[matchingPath];
      }
    }

    // If path is not in the map, allow access (e.g., other routes like /opportunity/:id)
    if (!moduleKey) {
      return;
    }

    // Check if user has access to this module
    const hasAccess = user.user_modules?.[moduleKey] === true;

    if (!hasAccess) {
      // Find the first available module to redirect to
      const availableModules = Object.entries(pathToModuleMap).find(([_, key]) => {
        return user.user_modules?.[key] === true;
      });

      if (availableModules) {
        navigate(availableModules[0], { replace: true });
      } else {
        // If no modules are available, redirect to login or show a message
        navigate('/login', { replace: true });
      }
    }
  }, [path, location.pathname, user.user_modules, navigate]);

  // Determine module key for rendering check
  let moduleKey: keyof NonNullable<IUser['user_modules']> | undefined = pathToModuleMap[path];
  if (!moduleKey) {
    const matchingPath = Object.keys(pathToModuleMap).find((modulePath) => {
      return location.pathname === modulePath || location.pathname.startsWith(`${modulePath}/`);
    });
    if (matchingPath) {
      moduleKey = pathToModuleMap[matchingPath];
    }
  }

  if (moduleKey && user.user_modules?.[moduleKey] !== true) {
    // Show loader while redirecting
    return <Loader message="Redirecting…" />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
